import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import SectionHeader from "../../components/SectionHeader";
import { createCourse } from "../../services/courseService";
import { getRecentDocuments } from "../../services/analyticsService";
import { useAuth } from "../../hooks/useAuth";
import { routePaths } from "../../constants/routes";
import "../../style/dashboard.css";

const quickActions = [
  "Review new demo enquiries",
  "Prepare next live class notes",
  "Update automation assignments",
  "Shortlist learners for mock interviews",
];

export default function TrainerDashboard() {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    getRecentDocuments("enquiries", 5)
      .then(setRecentLeads)
      .catch((error) => {
        console.warn("Unable to load recent trainer leads:", error);
      });
  }, []);

  const demoLeads = recentLeads.filter((lead) => ["demo", "course"].includes(lead.enquiryType));
  const supportLeads = recentLeads.filter((lead) => ["support", "contact"].includes(lead.enquiryType));
  const newsletterLeads = recentLeads.filter((lead) => lead.enquiryType === "newsletter");
  const trainerStats = [
    { label: "Fresh leads", value: recentLeads.length },
    { label: "Demo/course leads", value: demoLeads.length },
    { label: "Support/contact", value: supportLeads.length },
    { label: "Newsletter interest", value: newsletterLeads.length },
  ];

  const handleCreate = async (event) => {
    event.preventDefault();

    if (!title.trim() || !description.trim()) {
      toast.error("Please enter course title and description.");
      return;
    }

    setLoading(true);

    try {
      await createCourse({
        title: title.trim(),
        description: description.trim(),
        trainerId: user?.uid,
        trainerEmail: user?.email,
        status: "draft",
      });
      toast.success("Course draft created.");
      setTitle("");
      setDescription("");
    } catch (error) {
      toast.error(error.message || "Unable to create course.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="learner-dashboard">
      <section className="dashboard-hero surface-card">
        <div>
          <span className="ui-badge">Trainer Dashboard</span>
          <h1>Manage course content, leads, and learner follow-up.</h1>
          <p>
            Use this area to create course drafts, track fresh enquiries, and keep
            training actions moving without switching between too many pages.
          </p>
        </div>
        <div className="dashboard-profile-panel">
          <strong>Trainer workspace</strong>
          <span>{user?.email}</span>
          <span>Course drafts</span>
          <span>Lead follow-up</span>
          <NavLink to={routePaths.leadCrm}>Open Lead CRM</NavLink>
          <NavLink to={routePaths.bugs}>Open Bug Tracker</NavLink>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="dashboard-mini-stat-grid">
          {trainerStats.map((item) => (
            <article className="surface-card dashboard-mini-stat" key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="dashboard-section dashboard-split">
        <form className="surface-card dashboard-form-card" onSubmit={handleCreate}>
          <h2>Create course draft</h2>
          <label>
            Course Title
            <input
              className="input-ui"
              placeholder="Example: Selenium Automation with Java"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <label>
            Description
            <textarea
              className="textarea-ui"
              placeholder="What will learners achieve in this course?"
              rows="5"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          <button className="btn-ui" type="submit" disabled={loading}>
            {loading ? "Creating..." : "Create Draft"}
          </button>
        </form>

        <div className="surface-card dashboard-list-card">
          <h2>Today&apos;s trainer workflow</h2>
          <ul>
            {quickActions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="dashboard-section">
        <SectionHeader
          eyebrow="Recent Leads"
          title="Latest course or support enquiries"
          description="Use these as a quick trainer follow-up queue."
        />
        <div className="dashboard-lead-list">
          {recentLeads.length ? (
            recentLeads.map((lead) => (
              <article className="surface-card dashboard-lead-card" key={lead.id}>
                <div className="dashboard-lead-head">
                  <strong>{lead.name || "Website visitor"}</strong>
                  <em>{lead.enquiryType || "lead"}</em>
                </div>
                <span>{lead.email || lead.phone || "No contact detail"}</span>
                <p>{lead.courseInterest || lead.subject || lead.enquiryType || "General enquiry"}</p>
                <small>{lead.source || lead.sourcePath || "website"}</small>
              </article>
            ))
          ) : (
            <div className="surface-card dashboard-empty-card">
              No recent enquiries available yet.
            </div>
          )}
        </div>
      </section>

      <section className="dashboard-section dashboard-split reverse">
        <div className="surface-card dashboard-list-card">
          <h2>Trainer follow-up checklist</h2>
          <ul>
            <li>Call demo leads within 24 hours</li>
            <li>Share course roadmap after every enquiry</li>
            <li>Prepare assignments for active learners</li>
            <li>Schedule mock interviews for job-ready students</li>
          </ul>
        </div>
        <div className="surface-card dashboard-cta-card">
          <span className="ui-badge">Trainer requirement</span>
          <h2>Focus on demo conversion and student progress.</h2>
          <p>
            Use this dashboard to track new interest, create course drafts, and keep
            learner follow-up visible.
          </p>
        </div>
      </section>
    </main>
  );
}
